import React from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';

// What each search mode does and when to pick it.
export const MODE_HINTS = {
    hybride: {
        how: "Combine les deux méthodes : le sens du profil ET les mots-clés exacts de la mission.",
        when: "Le bon choix dans la plupart des cas. Commencez toujours par celui-ci.",
        example: "« Développeur Java Spring » trouve les profils Java/Spring, mais aussi des profils backend JVM proches.",
    },
    vecteur: {
        how: "Compare le sens global des CV avec la mission, même si les mots utilisés sont différents.",
        when: "Quand la mission est rare ou décrite avec un vocabulaire différent des CV, pour élargir le vivier.",
        example: "« Data Engineer » peut faire remonter des profils « ETL / Big Data / Spark » qui n'écrivent pas « Data Engineer ».",
    },
    texte: {
        how: "Cherche les mots-clés exacts de la mission dans les CV (technologies, certifications, intitulés).",
        when: "Quand une compétence précise est obligatoire et non négociable.",
        example: "« ISTQB », « SAP FICO » ou « Kubernetes » : seuls les CV qui contiennent ces mots remontent.",
    },
};

const chipSx = (active) => ({
    cursor: 'pointer', fontSize: '0.75rem', fontWeight: active ? 600 : 500,
    backgroundColor: active ? '#03407b' : '#f1f5f9',
    color: active ? '#fff' : '#64748b',
    '&:hover': { backgroundColor: active ? '#02357a' : '#e2e8f0' },
});

export const SectionTitle = ({ children, hint }) => (
    <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>{children}</Typography>
        {hint && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.4 }}>{hint}</Typography>}
    </Box>
);

export const HelpBox = ({ children, sx }) => (
    <Box sx={{ p: 1.25, borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.75, ...sx }}>
        {children}
    </Box>
);

export const ModeSelector = ({ filter, setFilter, title = 'Mode' }) => (
    <Box sx={{ mb: 2.5 }}>
        <SectionTitle>{title}</SectionTitle>
        <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            {Object.keys(MODE_HINTS).map((m) => (
                <Chip key={m} label={m} onClick={() => setFilter(m)} sx={{ ...chipSx(filter === m), flex: 1, textTransform: 'capitalize' }} />
            ))}
        </Box>
        {MODE_HINTS[filter] && (
            <HelpBox>
                <Typography sx={{ fontSize: '0.76rem', color: '#334155', lineHeight: 1.45 }}>
                    <b>Comment ça marche :</b> {MODE_HINTS[filter].how}
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', color: '#334155', lineHeight: 1.45 }}>
                    <b>À utiliser :</b> {MODE_HINTS[filter].when}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.45, fontStyle: 'italic' }}>
                    Ex. {MODE_HINTS[filter].example}
                </Typography>
            </HelpBox>
        )}
    </Box>
);

// Number picker: preset chips + a free value.
export const NumberChoice = ({ value, setValue, options, withOther = true }) => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {options.map((n) => (
            <Chip key={n} label={n} onClick={() => setValue(n)} sx={chipSx(value === n)} />
        ))}
        {withOther && (
            <TextField
                size="small"
                type="number"
                placeholder="Autre"
                value={options.includes(value) ? '' : value}
                onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v > 0) setValue(v); }}
                sx={{ width: 74, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} slotProps={{ htmlInput: { min: 1, style: { fontSize: '0.75rem', padding: '4px 8px' } } }}
            />
        )}
    </Box>
);

export const popoverPaperSx = { p: 2, width: 380, maxHeight: '80vh', overflowY: 'auto', borderRadius: '14px', mt: 1, border: '1px solid #e2e8f0', boxShadow: '0 12px 32px rgba(15,23,42,0.12)' };
